---
title: "SMS Terms & Conditions and Privacy Policy"
short: "Levis SMS Terms & Privacy"
keywords: "SMS terms, text messaging consent, privacy policy, TCPA compliance, opt-in, opt-out, Gage Levis System, mobile privacy"
seo:
  title: "SMS Terms & Conditions and Privacy Policy | Gage Technologies"
  description: "Read the SMS Terms & Conditions and Privacy Policy for the Gage Levis System SMS program, including opt-in, opt-out, message frequency, and data practices."
---

<Section>
  ## SMS Terms & Conditions and Privacy Policy

  **Gage Technologies** | Gage Levis System SMS Program

  **Last updated**: July 15, 2026
</Section>

<Section>
  ### SMS Terms & Conditions

  #### 1. Program Description

  Gage Technologies ("we," "us," "our") offers an SMS text messaging program that delivers updates and informational messages related to the Gage Levis System. By texting START to (855) 811-0028, you consent to receive recurring automated text messages from Gage Technologies at the mobile number you used to opt in.

  #### 2. Message Frequency

  Message frequency varies, but you can expect to receive up to 2-3 messages per week. Message frequency may change over time.

  #### 3. Cost

  Message and data rates may apply. Charges are billed by your mobile carrier according to your mobile plan. Gage Technologies does not charge for the messages themselves.

  #### 4. How to Opt Out

  You can cancel the SMS program at any time by texting STOP to (855) 811-0028. After you send STOP, we will send you a final message confirming that you have been unsubscribed. You will not receive further messages unless you opt in again by texting START.

  #### 5. Help and Support

  For assistance, text HELP to (855) 811-0028, email us at support@gagetech.com, or call (254) 772-3400.

  #### 6. Consent Not Required for Purchase

  Consent to receive text messages is not a condition of purchasing any goods or services from Gage Technologies.

  #### 7. Eligibility

  You must be 18 years of age or older and the account holder (or have the account holder's permission) for the mobile number used to enroll.

  #### 8. Carrier Disclaimer

  Carriers are not liable for delayed or undelivered messages. Message delivery is subject to effective transmission by your mobile carrier and is not guaranteed.

  #### 9. Changes to These Terms

  We may update these SMS Terms & Conditions at any time. Changes take effect when posted to this page. Your continued participation in the program after changes are posted constitutes acceptance of the revised terms.

  #### 10. Contact

  Gage Technologies
  215 Mary Ave Suite 304
  Waco, Texas 76701
</Section>

<Section>
  ### SMS Privacy Policy

  #### 1. Information We Collect

  When you opt in to the Gage Levis System SMS program, we collect your mobile phone number, the date and time of your opt-in, the keyword you texted, and your subsequent interactions with the program (such as replies of YES, HELP, or STOP).

  #### 2. How We Use Your Information

  We use this information solely to:

  - Deliver the updates and informational messages you signed up for
  - Confirm, manage, and honor your subscription preferences (including opt-outs)
  - Provide customer support when you request it
  - Comply with legal, regulatory, and carrier requirements, including maintaining records of consent

  #### 3. No Sharing of Opt-In Data

  No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Text messaging originator opt-in data and consent will not be shared with or sold to any third parties.

  We may share your information only with service providers who help us operate the SMS program (such as our messaging platform provider), and only to the extent necessary to deliver the service, or where required by law.

  #### 4. Data Retention

  We retain records of your opt-in consent and message history for as long as needed to operate the program and to satisfy legal and carrier compliance requirements. If you opt out, we retain your number on our suppression list to ensure we do not message you again.

  #### 5. Security

  We use commercially reasonable safeguards to protect the information collected through this program. However, no method of electronic transmission or storage is completely secure.

  #### 6. Your Choices

  You may opt out at any time by texting STOP to (855) 811-0028. You may also contact us at support@gagetech.com to request information about the data we hold in connection with this program or to request its deletion, subject to our legal retention obligations.

  #### 7. Changes to This Policy

  We may update this SMS Privacy Policy from time to time. The "Last updated" date at the top of this page reflects the most recent revision.

  #### 8. Contact Us

  Questions about this policy or the SMS program can be directed to:

  Gage Technologies
  215 Mary Ave Suite 304
  Waco, Texas 76701
  support@gagetech.com
  (254) 772-3400
</Section>

<Section data-texture>
  ## Questions About Our SMS Program?

  If you have any questions about these terms or our SMS practices, our team is here to help.

  <ButtonCTA href="/contact">Contact Us</ButtonCTA>
</Section>
